import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import { GenericChartCard } from '@components/ui/Chart';
import type { MetricSeriesConfig } from '@hooks/graph/useMetricChart';
import { Stack, Text } from '@mantine/core';
import { formatCount } from '@utils/currency';
import { useMemo, type FC, type ReactElement } from 'react';

import { dayLabel } from './dates';
import type { StoryDay } from './stockStory';
import classes from './StockStoryView.module.css';

const n = (v: number): string => formatCount(v, { maxDecimals: 0 });

type Point = StoryDay & { label: string };

export type StoryMark = { day: string; kind: 'start' | 'land' | 'dip'; qty?: number };

const MARK_COLOR: Record<StoryMark['kind'], string> = { start: '#1c7ed6', land: '#0ca678', dip: '#e03131' };

/**
 * One SKU's stock through the window, as an inventory step chart: each day's stock is held
 * flat for the day, falls by the day's demand into the next, and jumps on a landing. L and R
 * are flat levels at the window's average demand. Every production start, landing and dip is a
 * marked day. The table under it is the same numbers, day by day.
 */
const StockStoryView: FC<{ days: StoryDay[]; window: { from: string; to: string; lead: number }; period?: { start: string | null; end: string | null }; marks: StoryMark[]; levelDays: number; refillDays: number }> = ({ days, window, period, marks, levelDays, refillDays }) => {
    const avg = days.length ? days.reduce((s, d) => s + d.demand, 0) / days.length : 0;
    const L = avg * levelDays;
    const R = avg * refillDays;
    const points = useMemo<Point[]>(() => days.map((d) => ({ ...d, level: L, refill: R, label: dayLabel(d.day) })), [days, L, R]);
    const series = useMemo<MetricSeriesConfig<Point>[]>(
        () => [
            { id: 'withPlan', key: 'withPlan', label: 'Stock with the plan', color: '#212529', strokeWidth: 2.5, curve: 'stepAfter' },
            { id: 'without', key: 'without', label: 'Stock without it', color: '#adb5bd', variant: 'dashed', curve: 'stepAfter', hideDots: true },
            { id: 'level', key: 'level', label: `L · ${levelDays}d ≈ ${n(L)}`, color: '#fd7e14', variant: 'dashed', strokeWidth: 1.5, hideDots: true, curve: 'linear' },
            { id: 'refill', key: 'refill', label: `R · ${refillDays}d ≈ ${n(R)}`, color: '#0ca678', variant: 'dashed', strokeWidth: 1.5, hideDots: true, curve: 'linear' },
        ],
        [levelDays, refillDays, L, R],
    );
    const inWindow = (day: string): boolean => day >= window.from && day <= window.to;
    // One marked line per day, its events said together: "Lands 733 · Start 403".
    const lines = useMemo(() => {
        const byDay = new Map<string, StoryMark[]>();
        for (const m of marks) if (days.some((d) => d.day === m.day)) byDay.set(m.day, [...(byDay.get(m.day) ?? []), m]);
        return [...byDay.entries()]
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([day, ms]) => {
                const order: StoryMark['kind'][] = ['land', 'start', 'dip'];
                const sorted = [...ms].sort((a, b) => order.indexOf(a.kind) - order.indexOf(b.kind));
                const word = { start: 'Start', land: 'Lands', dip: 'Dips' } as const;
                return { x: dayLabel(day), label: sorted.map((m) => `${word[m.kind]}${m.qty ? ` ${n(m.qty)}` : ''}`).join(' · '), color: MARK_COLOR[sorted[0].kind] };
            });
    }, [marks, days]);
    const windowDemand = days.filter((d) => inWindow(d.day)).reduce((s, d) => s + d.demand, 0);

    const columns = useMemo<DataSheetColumn<Point>[]>(() => {
        const num = (key: keyof StoryDay, header: string, show = true): DataSheetColumn<Point> | null =>
            show ? { key: String(key), header, width: 100, align: 'right', render: (_v, r): ReactElement => <Text size="sm">{Number(r[key]) ? n(Number(r[key])) : '—'}</Text> } : null;
        const has = (key: keyof StoryDay): boolean => days.some((d) => Number(d[key]) > 0);
        const day: DataSheetColumn<Point> = { key: 'day', header: 'Day', width: 120, sticky: true, render: (_v, r): ReactElement => <Text size="sm" fw={inWindow(r.day) ? 700 : 400}>{r.label}</Text> };
        return [
            day,
            num('demand', 'Demand'),
            num('inProduction', 'In production', has('inProduction')),
            num('inTransit', 'In transit', has('inTransit')),
            num('onBook', 'Approved', has('onBook')),
            num('proposed', 'This plan', has('proposed')),
            num('without', 'Stock without'),
            num('withPlan', 'Stock with plan'),
            num('level', 'L'),
            num('refill', 'R'),
        ].filter((c): c is DataSheetColumn<Point> => c !== null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [days, window]);

    return (
        <Stack gap="sm">
            <GenericChartCard<Point>
                kind="line"
                title={`Stock, ${dayLabel(days[0]?.day)} → ${dayLabel(days[days.length - 1]?.day)}`}
                subtitle={`Demand this plan answers: ${dayLabel(window.from)} → ${dayLabel(window.to)}, ${n(windowDemand)} units`}
                data={points}
                dimensionKey="label"
                dimensionType="category"
                series={series}
                height={300}
                controls={{ showRange: false, showSeriesToggles: true, showToolbar: true }}
                referenceLines={lines}
                valueFormatter={n}
            />
            <Text className={classes.source}>
                {period?.start && period.end ? `The objective plans ${dayLabel(period.start)} → ${dayLabel(period.end)}; ` : ''}
                {window.lead > 0 ? `what starts on a day lands ${window.lead} day${window.lead === 1 ? '' : 's'} later, so the demand it answers is ${dayLabel(window.from)} → ${dayLabel(window.to)}. ` : `what is placed lands the same day, so the demand it answers is ${dayLabel(window.from)} → ${dayLabel(window.to)}. `}
                Where the numbers come from: the latest run stores this SKU&apos;s day-by-day projection on its requirement row. Demand per day is your demand settings (run rate per channel, plus committed shipments on their dispatch dates). On hand, in production (open work orders) and in transit (POs and transfers on the way) come from warehouse stock, landing on their expected dates. Each day shows stock at the start of the day, after anything landing that morning; demand then takes it down into the next day, never below zero. L and R are drawn flat at the window's average demand ({n(avg)}/day) × {levelDays} and {refillDays} days; the engine reads them against each day's demand ahead, so a heavy shipment day can move the dip by a day.
            </Text>
            <DataSheet<Point> data={points} columns={columns} getRowKey={(r) => r.day} getRowClassName={(r) => (inWindow(r.day) ? classes.inWindow : '')} rowHeight={34} hideHeaderBar maxHeight="auto" />
        </Stack>
    );
};

export default StockStoryView;
